import React, { useCallback, useContext } from 'react';
import styles from '../../../../../styles/components/Modal/Employees/components/PermissionGroupTab.module.css';
import { permissionTranslations, getPermissionLabel, getPermissionDescription } from '../../../../../utils/translations/permissionTranslations';
import { DropdownSelector } from '../../../../UI/DropdownSelector';
import { LangContext } from '../../../../../context/LangContext';
import { ThemeContext } from '../../../../../context/ThemeContext';

const SUFFIXES_FOR_DROPDOWN = ['.all', '.desk', '.assigned'];

export const PermissionGroupTab = ({
    groupName,
    groupDisplayName,
    permissions,
    selectedPermissions,
    handlePermissionChange,
    handlePermissionDropdownChange,
    isDark
}) => {
    const { langSelected } = useContext(LangContext);
    const pT = permissionTranslations;

    const safeSelectedPermissions = Array.isArray(selectedPermissions) ? selectedPermissions : [];

    const dropdownPermissionsMap = new Map();
    const regularPermissions = [];

    permissions.forEach(permission => {
        const matchingSuffix = SUFFIXES_FOR_DROPDOWN.find(suffix => permission.name.endsWith(suffix));

        if (matchingSuffix) {
            const baseName = permission.name.substring(0, permission.name.lastIndexOf('.'));
            if (!dropdownPermissionsMap.has(baseName)) {
                dropdownPermissionsMap.set(baseName, []);
            }
            dropdownPermissionsMap.get(baseName).push(permission);
        } else {
            regularPermissions.push(permission);
        }
    });

    const renderPermissionItem = useCallback((permission, isSelected) => {
        const translatedName = getPermissionLabel(permission, langSelected);
        const translatedDescription = getPermissionDescription(permission, langSelected);

        return (
            <div key={permission.name} className={`${styles.permissionItem} ${isDark ? styles.darkPermissionItem : ''}`}>
                <input
                    type="checkbox"
                    id={permission.name}
                    checked={isSelected}
                    onChange={(e) => handlePermissionChange(permission.name, e.target.checked)}
                    className={`${isDark ? styles.darkCheckbox : ''}`}
                />
                <label htmlFor={permission.name}>
                    <span className={`${styles.permissionName} ${isDark ? styles.darkPermissionName : ''}`}>
                        {translatedName}
                    </span>
                    {translatedDescription && (
                        <span className={`${styles.permissionDescription} ${isDark ? styles.darkPermissionDescription : ''}`}>
                            {translatedDescription}
                        </span>
                    )}
                </label>
            </div>
        );
    }, [handlePermissionChange, langSelected, isDark]);

    return (
        <div className={`${styles.container} ${isDark ? styles.darkContainer : ''}`}>
            <h2 className={`${isDark ? styles.darkHeading : ''}`}>{groupDisplayName}</h2>
            <div className={styles.permissionList}>
                {regularPermissions.map(permission => {
                    const isSelected = safeSelectedPermissions.includes(permission.name);
                    return renderPermissionItem(permission, isSelected);
                })}

                {Array.from(dropdownPermissionsMap.entries()).map(([baseName, dropdownPerms]) => {
                    const dropdownOptions = dropdownPerms.map(permission => ({
                        value: permission.name,
                        label: getPermissionLabel(permission, langSelected),
                        description: getPermissionDescription(permission, langSelected)
                    }));

                    const currentSelectedDropdownValue = safeSelectedPermissions.find(p => p.startsWith(baseName + '.')) || null;

                    const dropdownFilterName = pT.names?.[baseName]?.[langSelected] || baseName;

                    return (
                        <div key={baseName} className={`${styles.dropdownWrapper} ${isDark ? styles.darkDropdownWrapper : ''}`}>
                            <p className={`${styles.dropdownLabel} ${isDark ? styles.darkDropdownLabel : ''}`}>{dropdownFilterName}</p>
                            <DropdownSelector
                                filterName={dropdownFilterName}
                                filterKey={baseName}
                                selectedValue={currentSelectedDropdownValue}
                                handleFilterChange={handlePermissionDropdownChange}
                                options={dropdownOptions}
                                hasError={false}
                                errorMessage={""}
                                isDark={isDark}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
